import { EWallFaceLayout, IBaseDataEdgeFace, IFaceData, IFaceHolePoint } from '@/editor/data/face/face';
import { IWindowData } from '@/editor/data/model/model';
import { clamp } from 'lodash-es';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { FloatingPoint } from '@/editor/math/number';
import { EFaceType } from '@/editor/entities/type';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getArcEdgeByRectBox } from '../arc';
import { LineSeg2d } from '@/editor/math/LineSeg2d';

export function getFaceWindowHoles(
  windowDatas: IWindowData[],
  faceBaseData: IBaseDataEdgeFace & { center?: IPoint2d; clockwise?: boolean }
) {
  const { start: faceArcStart, end: faceArcEnd, center: faceArcCenter, clockwise } = faceBaseData;
  const limitYPos = faceBaseData.height + faceBaseData.offGround;

  // 直墙面情况
  if (!faceArcCenter || clockwise === undefined)
    return windowDatas
      .map((windowData) => {
        // 门窗离地高度超过墙体高度时，无需进行开洞
        if (windowData.offGround > faceBaseData.height) return undefined;

        const edgeSeg = new LineSeg2d(faceBaseData.start, faceBaseData.end);
        const windowCenterRatioOnSeg = edgeSeg.getFootRatio(windowData.center);
        const windowProportionOnSeg = windowData.width / edgeSeg.length;
        const windowStart = edgeSeg.getPointFromRatio(windowCenterRatioOnSeg - windowProportionOnSeg / 2);
        const windowEnd = edgeSeg.getPointFromRatio(windowCenterRatioOnSeg + windowProportionOnSeg / 2);
        const overlapSegment = edgeSeg.getOverlapSegment(new LineSeg2d(windowStart, windowEnd));

        if (!overlapSegment) return undefined;

        const [startRatio, endRatio] = [
          edgeSeg.getPointParameter(overlapSegment.start),
          edgeSeg.getPointParameter(overlapSegment.end)
        ].sort((a, b) => a - b);

        return [
          { ratioX: startRatio, y: Math.min(windowData.offGround, limitYPos) },
          { ratioX: startRatio, y: Math.min(windowData.offGround + windowData.height, limitYPos) },
          { ratioX: endRatio, y: Math.min(windowData.offGround + windowData.height, limitYPos) },
          { ratioX: endRatio, y: Math.min(windowData.offGround, limitYPos) }
        ];
      })
      .filter((hole) => !!hole) satisfies IFaceHolePoint[][];

  // 弧墙面情况
  const faceArc = new ArcSeg2d({ start: faceArcStart, end: faceArcEnd, center: faceArcCenter, clockwise: clockwise });
  const faceCircle = faceArc.createCircle();
  return windowDatas
    .map((windowData) => {
      const { center: windowCenter, width: windowWidth, height: windowHeight, offGround: windowOffGround } = windowData;
      // 门窗离地高度超过墙体高度时，无需进行开洞
      if (windowOffGround > faceBaseData.height) return undefined;

      const edge = getArcEdgeByRectBox({ rectCenter: windowCenter, rectWidth: windowWidth, circle: faceCircle });
      if (!edge) return null;

      const overlapSeg = faceArc.getOverlapSegment(new ArcSeg2d(edge));

      if (!overlapSeg) return undefined;

      const [startRatio, endRatio] = [
        clamp(faceArc.getFootRatio(edge.start), 0, 1),
        clamp(faceArc.getFootRatio(edge.end), 0, 1)
      ].sort((a, b) => a - b);

      return [
        { ratioX: startRatio, y: Math.min(windowOffGround, limitYPos) },
        { ratioX: startRatio, y: Math.min(windowOffGround + windowHeight, limitYPos) },
        { ratioX: endRatio, y: Math.min(windowOffGround + windowHeight, limitYPos) },
        { ratioX: endRatio, y: Math.min(windowOffGround, limitYPos) }
      ];
    })
    .filter((hole) => !!hole);
}

/** 洞口顶点的 x 存的是比例，不能复用 IPoint2d 的 isSimilarOutlines */
export function isSimilarFaceHoles(holes1: IFaceHolePoint[][], holes2: IFaceHolePoint[][]) {
  if (holes1.length !== holes2.length) return false;

  return holes1.every((hole, holeIndex) => {
    const otherHole = holes2[holeIndex];
    if (hole.length !== otherHole.length) return false;

    return hole.every(
      (point, pointIndex) =>
        FloatingPoint.equal(point.ratioX, otherHole[pointIndex].ratioX) &&
        FloatingPoint.equal(point.y, otherHole[pointIndex].y)
    );
  });
}

/**
 * 获取圆周轮廓(逆时针方向)
 * @param center 圆心
 * @param radius 半径
 * @param count 切分份数
 * @returns 轮廓点
 */
export function getCircleContour(center: IPoint2d, radius: number, count: number) {
  const contour: IPoint2d[] = [];
  const deltaAngle = (Math.PI * 2) / count;
  for (let i = 0; i < count; i++) {
    const angle = deltaAngle * i;
    contour.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle)
    });
  }

  return contour;
}

export function isWallFace(fd: IFaceData) {
  return fd.type === EFaceType.WallFace || fd.type === EFaceType.SepWallFace;
}

export function isColumnFace(fd: IFaceData) {
  return fd.type === EFaceType.ColumnFace;
}

export function isMaterialNotReplaceable(fd: IFaceData) {
  return (
    fd.relativeInfo.sepType === ESeparateWallType.GlassWall ||
    (fd.relativeInfo.sepType === ESeparateWallType.HalfGlassWall && fd.relativeInfo.layout === EWallFaceLayout.MIDDLE)
  );
}
